import type React from 'react'
import { ArrowRightOutlined } from '@ant-design/icons'
import { Avatar } from 'antd'
import { Horizontal, Vertical, Texto, BBDTag, MaterialIcon, GraviButton } from '@gravitate-js/excalibrr'
import { getActionTagProps, getActionIcon } from '../utils/activityHelpers'

interface RecentActivityItemProps {
  invoiceId: string // DOM ID (for React keys)
  displayId: string // Display ID (shown in UI)
  actionType: string // Action type from API (invoice_added, voided, etc.)
  description: string
  personName: string
  timestamp: string
  onClick?: (event: React.MouseEvent) => void
}

export function RecentActivityItem({
  invoiceId,
  displayId,
  actionType,
  description,
  personName,
  timestamp,
  onClick,
}: RecentActivityItemProps) {
  // Get action-based tag props and icon from helpers
  const tagProps = getActionTagProps(actionType)
  const iconName = getActionIcon(actionType)

  return (
    <div
      style={{
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
        padding: '16px',
        backgroundColor: '#fff',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease',
        width: '100%',
        minWidth: '280px',
      }}
      className={onClick ? 'hover:shadow-sm hover:border-gray-300' : ''}
      onClick={onClick}
    >
      {/* Top Row: Invoice ID, Status, Arrow */}
      <Horizontal justifyContent='space-between' alignItems='center' style={{ marginBottom: '8px' }}>
        <Horizontal gap={8} alignItems='center'>
          <Texto category='h5' weight='600' style={{ textDecoration: 'underline' }}>
            {displayId}
          </Texto>
          <BBDTag
            style={{ width: 'min-content' }}
            error={tagProps.error}
            warning={tagProps.warning}
            success={tagProps.success}
            theme2={tagProps.theme2}
            icon={<MaterialIcon icon={iconName} fill size={8} className='mr-2' />}
          >
            {tagProps.text}
          </BBDTag>
        </Horizontal>

        {onClick && (
          <MaterialIcon
            icon='arrow_forward'
            size={20}
            style={{
              color: '#6C757D',
              cursor: 'pointer',
              transition: 'color 0.2s ease',
            }}
            className='hover:text-gray-700'
          />
        )}
      </Horizontal>

      {/* Middle Row: Description */}
      <div style={{ marginBottom: '8px' }}>
        <Texto
          category='p1'
          appearance='secondary'
          style={{
            lineHeight: '1.4',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {description}
        </Texto>
      </div>

      {/* Bottom Row: Person Name, Timestamp */}
      <Horizontal justifyContent='space-between' alignItems='center'>
        <Horizontal gap={6} alignItems='center'>
          <Avatar size={20} style={{ backgroundColor: '#1890ff' }}>
            {personName.charAt(0).toUpperCase()}
          </Avatar>
          <Texto category='label' weight='600'>
            {personName}
          </Texto>
        </Horizontal>
        <Texto category='label' appearance='secondary'>
          {timestamp}
        </Texto>
      </Horizontal>
    </div>
  )
}
